import type { VaultChunk } from "@/types/vault";

/**
 * Generate a deterministic extractive summary from chunks.
 * In Strict mode, we never send raw excerpts, only summaries.
 */
export function generateLocalSummary(
  chunks: VaultChunk[],
  maxSentences: number = 5
): string {
  if (chunks.length === 0) {
    return "No relevant information found.";
  }

  // Simple extractive summary: take first sentences from top chunks
  const sentences: string[] = [];

  for (const chunk of chunks.slice(0, 3)) {
    // Split by sentence endings
    const chunkSentences = chunk.text
      .split(/[.!?]+\s+/)
      .filter((s) => s.trim().length > 20) // Filter very short fragments
      .slice(0, 2); // Take first 2 sentences from each chunk

    sentences.push(...chunkSentences);
  }

  // Limit total sentences
  const summary = sentences.slice(0, maxSentences).join(". ") + ".";

  return summary.length > 500 ? summary.substring(0, 500) + "..." : summary;
}

/**
 * Estimate token count (rough approximation: 1 token ≈ 4 characters)
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Limit context to stay within token budget
 */
export function limitContext(
  chunks: Array<{ text: string; source: string }>,
  maxTokens: number = 2500
): Array<{ text: string; source: string }> {
  let totalTokens = 0;
  const limited: Array<{ text: string; source: string }> = [];

  for (const chunk of chunks) {
    const chunkTokens = estimateTokens(chunk.text);
    if (totalTokens + chunkTokens > maxTokens) {
      break;
    }
    limited.push(chunk);
    totalTokens += chunkTokens;
  }

  return limited;
}

